import { List, Map } from 'immutable';

import { shapeEbayDetailsForAPI, shapeFaireDistanceUnitForApi, shapeFaireMassUnitForApi, shapeFairePricesForApi, shapePersonalizationQuestionsForAPI, shapeTaxonomyAttributesForAPI } from '@/utils/product/shapeAttributes';
import { getInventoryOperationValue } from '@/utils/product/getOperationValue';
import { tagsToArray, tagsToList } from '@/utils/tags';
import { isUploadFilesSupported } from '@/utils/product/checkAttribute';
import { shapeVariationsForAPI } from '@/utils/variations/shapeForAPI';
import { getDateKey } from '@/utils/time';
import { getSize } from '@/utils/iterable/getSize';
import { keepOld } from '@/utils/iterable/keepOld';
import { filter } from '@/utils/iterable/filter';
import { get } from '@/utils/iterable/get';

import { AMOUNT, ENABLED, NEW, OPERATIONS, OVERSET, REQUIRED, SELLING_METHOD, VALUE } from '@/constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { COMBINATIONS } from '@/constants/variations';
import { EMPTY, VELA } from '@/constants';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { STATUS } from '@/constants/listings';
import { FORM } from '@/constants/forms';

export function filterOperations(parameter, equals = true) {
  return function filterOperation(operation) {
    let match = !equals;

    if (typeof parameter === 'function') {
      match = parameter(operation);
    } else if (typeof parameter === 'string') {
      match = operation.get('type') === parameter;
    } else if (parameter instanceof RegExp) {
      match = parameter.test(operation.get('type'));
    }

    return match === equals;
  };
}

export function replaceOperation(operations = EMPTY.LIST, operation) {
  return operations.filter(filterOperations(operation.get('type'), false)).push(operation.delete('products'));
}

export function replacePhotosOperation({ operations, product }) {
  const channel = product.get('channel');

  function filterAllPhotosOperations(item) {
    switch (item.get('type')) {
      case OPERATIONS[channel].PHOTOS.ADD:
      case OPERATIONS[channel].PHOTOS.CHANGE_TO:
      case OPERATIONS[channel].PHOTOS.DELETE:
      case OPERATIONS[channel].PHOTOS.REPLACE:
      case OPERATIONS[channel].PHOTOS.SWAP:
      case OPERATIONS[channel].PHOTOS.UPDATE: {
        return true;
      }

      default: {
        return false;
      }
    }
  }

  function filterSameUpdateOperation(target) {
    function getAttributes(operaiton) {
      return operaiton.getIn(['value', 'attributes']).keySeq().toSet();
    }

    return function filterOperation(operaiton) {
      return (
        operaiton.get('type') === target.get('type') &&
        operaiton.getIn(['value', 'index']) === target.getIn(['value', 'index']) &&
        getSize(getAttributes(operaiton).subtract(getAttributes(target))) === 0
      );
    };
  }

  function reduceOperations(result, operation) {
    switch (operation.get('type')) {
      case OPERATIONS[channel].PHOTOS.CHANGE_TO: {
        return result
          .filter(filterOperations(filterAllPhotosOperations, false))
          .push(operation.delete('products'));
      }

      case OPERATIONS[channel].PHOTOS.UPDATE: {
        return result
          .filter(filterOperations(filterSameUpdateOperation(operation), false))
          .push(operation.delete('products'));
      }

      default: {
        return replaceOperation(result, operation);
      }
    }
  }

  return operations.reduce(reduceOperations, product.get('operations', EMPTY.LIST));
}

export function getProductOperations({ channel, userFeatures }) {
  switch (channel) {
    case EBAY: {
      return [
        OPERATIONS[EBAY].PHOTOS.CHANGE_TO,
        OPERATIONS[EBAY].VIDEOS.CHANGE_TO,
        OPERATIONS[EBAY].TITLE,
        OPERATIONS[EBAY].DESCRIPTION,
        OPERATIONS[EBAY].ITEM_CATEGORY,
        OPERATIONS[EBAY].CONDITION_ID,
        OPERATIONS[EBAY].CONDITION_DESCRIPTION,
        OPERATIONS[EBAY].VARIATIONS,
        OPERATIONS[EBAY].PRICE,
        OPERATIONS[EBAY].QUANTITY,
        OPERATIONS[EBAY].SKU,
        OPERATIONS[EBAY].PAYMENT_POLICY,
        OPERATIONS[EBAY].RETURN_POLICY,
        OPERATIONS[EBAY].SHIPPING_POLICY,
        OPERATIONS[EBAY].COUNTRY,
        OPERATIONS[EBAY].LOCATION,
        OPERATIONS[EBAY].POSTAL_CODE,
        OPERATIONS[EBAY].IRREGULAR,
        OPERATIONS[EBAY].WEIGHT_MAJOR,
        OPERATIONS[EBAY].WEIGHT_MINOR,
        OPERATIONS[EBAY].DEPTH,
        OPERATIONS[EBAY].LENGTH,
        OPERATIONS[EBAY].WIDTH,
        OPERATIONS[EBAY].ITEM_SPECIFICS.SET,
        OPERATIONS[EBAY].PRODUCT_DETAILS.SET,
        OPERATIONS[EBAY].STORE_CATEGORY,
      ];
    }

    case ETSY: {
      const operations = [
        OPERATIONS[ETSY].PHOTOS.CHANGE_TO,
        OPERATIONS[ETSY].VIDEOS.CHANGE_TO,
        OPERATIONS[ETSY].TITLE,
        OPERATIONS[ETSY].DESCRIPTION,
        OPERATIONS[ETSY].TAGS.CHANGE_TO,
        OPERATIONS[ETSY].WHO_MADE,
        OPERATIONS[ETSY].IS_SUPPLY,
        OPERATIONS[ETSY].WHEN_MADE,
        OPERATIONS[ETSY].CATEGORY,
        OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES,
        OPERATIONS[ETSY].SHOULD_AUTO_RENEW,
        OPERATIONS[ETSY].DIGITAL,
        OPERATIONS[ETSY].FILES.CHANGE_TO,
        OPERATIONS[ETSY].PRODUCTION_PARTNERS,
        OPERATIONS[ETSY].SECTION,
        OPERATIONS[ETSY].MATERIALS.CHANGE_TO,
        OPERATIONS[ETSY].VARIATIONS,
        OPERATIONS[ETSY].PRICE,
        OPERATIONS[ETSY].QUANTITY,
        OPERATIONS[ETSY].SKU,
        OPERATIONS[ETSY].PROCESSING_PROFILE,
        OPERATIONS[ETSY].SHIPPING_PROFILE,
        OPERATIONS[ETSY].WEIGHT,
        OPERATIONS[ETSY].LENGTH,
        OPERATIONS[ETSY].WIDTH,
        OPERATIONS[ETSY].HEIGHT,
        OPERATIONS[ETSY].RETURN_POLICY,
      ];

      if (!userFeatures.get(FEATURE.ETSY_PERSONALIZATION_QUESTIONS)) {
        operations.push(
          OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET,
          OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET,
          OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET,
          OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET,
        );
      } else {
        operations.push(
          OPERATIONS[ETSY].PERSONALIZATION_QUESTIONS,
        );
      }

      return operations;
    }

    case FAIRE: {
      return [
        OPERATIONS[FAIRE].PHOTOS.CHANGE_TO,
        OPERATIONS[FAIRE].TITLE,
        OPERATIONS[FAIRE].DESCRIPTION,
        OPERATIONS[FAIRE].CATEGORY,
        OPERATIONS[FAIRE].VARIATIONS,
        OPERATIONS[FAIRE].PRICES,
        OPERATIONS[FAIRE].QUANTITY,
        OPERATIONS[FAIRE].RESTOCK_DATE,
        OPERATIONS[FAIRE].SKU,
        OPERATIONS[FAIRE].GTIN,
        OPERATIONS[FAIRE].CONTINUE_SELLING,
        OPERATIONS[FAIRE].PREORDER,
        OPERATIONS[FAIRE].START_DATE,
        OPERATIONS[FAIRE].END_DATE,
        OPERATIONS[FAIRE].DEADLINE_DATE,
        OPERATIONS[FAIRE].KEEP_ACTIVE,
        OPERATIONS[FAIRE].CASE_SIZE,
        OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY,
        OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY,
        OPERATIONS[FAIRE].TARIFF_CODE,
        OPERATIONS[FAIRE].MASS_UNIT,
        OPERATIONS[FAIRE].WEIGHT,
        OPERATIONS[FAIRE].DISTANCE_UNIT,
        OPERATIONS[FAIRE].HEIGHT,
        OPERATIONS[FAIRE].LENGTH,
        OPERATIONS[FAIRE].WIDTH,
      ];
    }

    case SHOPIFY: {
      return [
        OPERATIONS[SHOPIFY].PHOTOS.CHANGE_TO,
        OPERATIONS[SHOPIFY].VIDEOS.CHANGE_TO,
        OPERATIONS[SHOPIFY].TITLE,
        OPERATIONS[SHOPIFY].DESCRIPTION,
        OPERATIONS[SHOPIFY].TAGS.CHANGE_TO,
        OPERATIONS[SHOPIFY].PRODUCT_TYPE,
        OPERATIONS[SHOPIFY].VENDOR,
        OPERATIONS[SHOPIFY].COLLECTIONS,
        OPERATIONS[SHOPIFY].CATEGORY,
        OPERATIONS[SHOPIFY].VARIATIONS,
        OPERATIONS[SHOPIFY].PAGE_TITLE,
        OPERATIONS[SHOPIFY].META_DESCRIPTION,
      ];
    }

    default: {
      return [];
    }
  }
}

export function getOperationsFromProduct({
  operation: source = {},
  operationTypes,
  product,
  toImmutable = true,
}) {
  const result = toImmutable ? EMPTY.LIST : [];

  if (getSize(operationTypes) === 0) return result;

  const channel = product.get('channel');

  function reduceOperationTypes(operations, type) {
    function addOperation(operationData) {
      const operation = { ...source, ...operationData };
      return toImmutable
        ? operations.push(Map(operation))
        : [...operations, operation];
    }

    function wrapInventoryOperationValue(value) {
      return toImmutable
        ? List([Map({ combination: null, offeringId: null, value })])
        : [{ combination: null, offeringId: null, value }];
    }

    switch (channel) {
      case EBAY: {
        switch (type) {
          case OPERATIONS[EBAY].PHOTOS.CHANGE_TO: {
            const value = product.getIn([FORM.PHOTOS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[EBAY].VIDEOS.CHANGE_TO: {
            const value = product.getIn([FORM.VIDEOS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[EBAY].TITLE: {
            const value = product.getIn([FORM.TITLE, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].DESCRIPTION: {
            const value = product.getIn([FORM.DESCRIPTION, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].ITEM_CATEGORY: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].STORE_CATEGORY: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.STORE_CATEGORY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].CONDITION_ID: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].CONDITION_DESCRIPTION: {
            const condition = product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE]);
            const description = product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, ATTRIBUTE.DESCRIPTION]);
            return condition && condition !== '1000' && description
              ? addOperation({ type, value: description })
              : operations;
          }

          case OPERATIONS[EBAY].VARIATIONS: {
            if (getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS])) === 0) {
              return operations;
            }

            return addOperation({ type, value: get('value')(shapeVariationsForAPI({ product, toImmutable })) });
          }

          case OPERATIONS[EBAY].PRICE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({
              type,
              value: getInventoryOperationValue({ channel, toImmutable, type: ATTRIBUTE.PRICE, value }),
            });
          }

          case OPERATIONS[EBAY].QUANTITY: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[EBAY].SKU: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[EBAY].PAYMENT_POLICY: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.PAYMENT_POLICY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].RETURN_POLICY: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].SHIPPING_POLICY: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.SHIPPING_POLICY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].COUNTRY: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.COUNTRY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].LOCATION: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.LOCATION, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].POSTAL_CODE: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.POSTAL_CODE, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].IRREGULAR: {
            return addOperation({ type, value: product.getIn([FORM.SHIPPING, ATTRIBUTE.IRREGULAR, VALUE]) });
          }

          case OPERATIONS[EBAY].WEIGHT_MAJOR: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.WEIGHT_MAJOR, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].WEIGHT_MINOR: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.WEIGHT_MINOR, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].DEPTH: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.DEPTH, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].LENGTH: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.LENGTH, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].WIDTH: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.WIDTH, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].ITEM_SPECIFICS.SET: {
            const value = shapeEbayDetailsForAPI({
              details: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP),
              toImmutable,
            });

            return getSize(value) > 0
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].ITEM_SPECIFICS.UPDATE: {
            const value = shapeEbayDetailsForAPI({
              details: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP),
              toImmutable,
              toList: false,
            });

            return getSize(value) > 0
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].PRODUCT_DETAILS.SET: {
            const value = shapeEbayDetailsForAPI({
              details: product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP),
              toImmutable,
            });

            return getSize(value) > 0
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[EBAY].PRODUCT_DETAILS.UPDATE: {
            const value = shapeEbayDetailsForAPI({
              details: product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP),
              toImmutable,
              toList: false,
            });

            return getSize(value) > 0
              ? addOperation({ type, value })
              : operations;
          }

          default: {
            return operations;
          }
        }
      }

      case ETSY: {
        switch (type) {
          case OPERATIONS[ETSY].PHOTOS.CHANGE_TO: {
            const value = product.getIn([FORM.PHOTOS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[ETSY].VIDEOS.CHANGE_TO: {
            const value = product.getIn([FORM.VIDEOS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[ETSY].TITLE: {
            const value = product.getIn([FORM.TITLE, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].DESCRIPTION: {
            const value = product.getIn([FORM.DESCRIPTION, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].TAGS.CHANGE_TO: {
            const tags = product.getIn([FORM.TAGS, VALUE], EMPTY.ORDERED_MAP);
            const newTags = product.getIn([FORM.TAGS, NEW], EMPTY.ORDERED_MAP);
            const value = tags.mergeWith(keepOld, newTags);

            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? tagsToList({ tags: value }) : tagsToArray(value) })
              : operations;
          }

          case OPERATIONS[ETSY].WHO_MADE: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, VALUE]);
            return value !== null
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].IS_SUPPLY: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, VALUE]);
            return value !== null
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].WHEN_MADE: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE]);
            return value !== null
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].CATEGORY: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], AMOUNT.ZERO);
            return value && value !== AMOUNT.ZERO
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES: {
            const value = shapeTaxonomyAttributesForAPI({
              taxonomyAttributes: product.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES]),
              toImmutable,
            });

            return getSize(value) > 0
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].SHOULD_AUTO_RENEW: {
            return addOperation({ type, value: product.getIn([FORM.DETAILS, ATTRIBUTE.SHOULD_AUTO_RENEW, VALUE]) });
          }

          case OPERATIONS[ETSY].DIGITAL: {
            return addOperation({ type, value: product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]) });
          }

          case OPERATIONS[ETSY].FILES.CHANGE_TO: {
            if (!isUploadFilesSupported({ product })) return operations;

            const value = product.getIn([FORM.FILES, VALUE]);

            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[ETSY].PRODUCTION_PARTNERS: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toArray() })
              : operations;
          }

          case OPERATIONS[ETSY].SECTION: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].MATERIALS.CHANGE_TO: {
            const value = product
              .getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, VALUE])
              .mergeWith(
                keepOld,
                product.getIn([FORM.DETAILS, ATTRIBUTE.MATERIALS, NEW], EMPTY.ORDERED_MAP)
              );

            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? tagsToList({ tags: value }) : tagsToArray(value) })
              : operations;
          }

          case OPERATIONS[ETSY].VARIATIONS: {
            if (
              product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]) ||
              getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS])) === 0
            ) {
              return operations;
            }

            return addOperation({ type, value: get('value')(shapeVariationsForAPI({ product, toImmutable })) });
          }

          case OPERATIONS[ETSY].PRICE: {
            if (
              product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]) ||
              !product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE])
            ) {
              return operations;
            }

            const operationValue = [
              getInventoryOperationValue({
                channel,
                inventory: product.get(FORM.INVENTORY),
                toImmutable,
                type: ATTRIBUTE.PRICE,
              }),
            ];

            return addOperation({
              type,
              value: toImmutable ? List(operationValue) : operationValue,
            });
          }

          case OPERATIONS[ETSY].QUANTITY: {
            if (
              product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]) ||
              !product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE])
            ) {
              return operations;
            }

            const operationValue = [
              getInventoryOperationValue({
                channel,
                inventory: product.get(FORM.INVENTORY),
                toImmutable,
                type: ATTRIBUTE.QUANTITY,
              }),
            ];

            return addOperation({
              type,
              value: toImmutable ? List(operationValue) : operationValue,
            });
          }

          case OPERATIONS[ETSY].SKU: {
            if (
              product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]) ||
              !product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE])
            ) {
              return operations;
            }

            const operationValue = [
              getInventoryOperationValue({
                channel,
                inventory: product.get(FORM.INVENTORY),
                toImmutable,
                type: ATTRIBUTE.SKU,
              }),
            ];

            return addOperation({
              type,
              value: toImmutable ? List(operationValue) : operationValue,
            });
          }

          case OPERATIONS[ETSY].PERSONALIZATION.IS_PERSONALIZABLE_SET: {
            return addOperation({
              type,
              value: product.getIn([FORM.PERSONALIZATION, ENABLED]),
            });
          }

          case OPERATIONS[ETSY].PERSONALIZATION.IS_REQUIRED_SET: {
            return product.getIn([FORM.PERSONALIZATION, ENABLED])
              ? addOperation({
                type,
                value: product.getIn([FORM.PERSONALIZATION, REQUIRED]),
              })
              : operations;
          }

          case OPERATIONS[ETSY].PERSONALIZATION.CHAR_COUNT_MAX_SET: {
            const enabled = product.getIn([FORM.PERSONALIZATION, ENABLED]);
            const value = product.getIn([FORM.PERSONALIZATION, ATTRIBUTE.CHAR_COUNT_MAX, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].PERSONALIZATION.INSTRUCTIONS_SET: {
            const enabled = product.getIn([FORM.PERSONALIZATION, ENABLED]);
            const value = product.getIn([FORM.PERSONALIZATION, ATTRIBUTE.INSTRUCTIONS, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].PERSONALIZATION_QUESTIONS: {
            const questions = product.getIn([FORM.PERSONALIZATION, VALUE]);

            if (getSize(questions) === 0) {
              return operations;
            }

            return addOperation({ type, value: shapePersonalizationQuestionsForAPI({ questions, toImmutable }) });
          }

          case OPERATIONS[ETSY].PROCESSING_PROFILE: {
            const digital = product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE, VALUE]);
            const overset = product.getIn([FORM.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE, OVERSET]);

            if (digital || overset || !value) {
              return operations;
            }

            return addOperation({ type, value: wrapInventoryOperationValue(value) });
          }

          case OPERATIONS[ETSY].SHIPPING_PROFILE: {
            const enabled = !product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].WEIGHT: {
            const enabled = !product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.WEIGHT, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].LENGTH: {
            const enabled = !product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.LENGTH, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].WIDTH: {
            const enabled = !product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.WIDTH, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].HEIGHT: {
            const enabled = !product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.HEIGHT, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[ETSY].RETURN_POLICY: {
            const enabled = !product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE]);
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE]);

            return enabled && value
              ? addOperation({ type, value })
              : operations;
          }

          default: {
            return operations;
          }
        }
      }

      case FAIRE: {
        switch (type) {
          case OPERATIONS[FAIRE].PHOTOS.CHANGE_TO: {
            const value = product.getIn([FORM.PHOTOS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[FAIRE].TITLE: {
            const value = product.getIn([FORM.TITLE, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[FAIRE].DESCRIPTION: {
            const value = product.getIn([FORM.DESCRIPTION, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[FAIRE].CATEGORY: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[FAIRE].PRICES: {
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, OVERSET]);

            if (overset) {
              return operations;
            }

            const inventory = product.get(FORM.INVENTORY);
            const value = filter(shapeFairePricesForApi({ inventory, toImmutable }), get('value'));

            if (getSize(value) === 0) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].QUANTITY: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].RESTOCK_DATE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.RESTOCK_DATE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.RESTOCK_DATE, OVERSET]);
            const quantityValue = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]);

            if (overset || !value || quantityValue !== AMOUNT.ZERO) {
              return operations;
            }

            return addOperation({ type, value: value.toISOString() });
          }

          case OPERATIONS[FAIRE].SKU: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].GTIN: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.GTIN, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.GTIN, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].CONTINUE_SELLING: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.CONTINUE_SELLING, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].PREORDER: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].START_DATE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.START_DATE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value: getDateKey(value) });
          }

          case OPERATIONS[FAIRE].END_DATE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.END_DATE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value: getDateKey(value) });
          }

          case OPERATIONS[FAIRE].DEADLINE_DATE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.DEADLINE_DATE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET]);
            const keepActive = product.getIn([FORM.INVENTORY, ATTRIBUTE.KEEP_ACTIVE, VALUE]);

            if (overset || keepActive || !value) {
              return operations;
            }

            return addOperation({ type, value: getDateKey(value) });
          }

          case OPERATIONS[FAIRE].KEEP_ACTIVE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.KEEP_ACTIVE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PREORDER, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].CASE_SIZE: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.CASE_SIZE, VALUE]);
            const sellingMethod = product.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE]);

            if (sellingMethod !== SELLING_METHOD.CASE || !value || parseInt(value) <= 1) {
              return operations;
            }

            return addOperation({ type, value: parseInt(value) });
          }

          case OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]);

            if (!value) {
              return operations;
            }

            return addOperation({ type, value: parseInt(value) });
          }

          case OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY: {
            const value = product.getIn([FORM.SHIPPING, ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]);
            const sellingMethod = product.getIn([FORM.SHIPPING, ATTRIBUTE.SELLING_METHOD, VALUE]);

            if (sellingMethod !== SELLING_METHOD.SIZE || !value) {
              return operations;
            }

            return addOperation({ type, value: parseInt(value) });
          }

          case OPERATIONS[FAIRE].TARIFF_CODE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.TARIFF_CODE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.TARIFF_CODE, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].DISTANCE_UNIT: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.DISTANCE_UNIT, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.HEIGHT, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({ type, value: shapeFaireDistanceUnitForApi(value) });
          }

          case OPERATIONS[FAIRE].HEIGHT: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.HEIGHT, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.HEIGHT, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].LENGTH: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.LENGTH, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.LENGTH, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].VARIATIONS: {
            if (getSize(product.getIn([FORM.VARIATIONS, COMBINATIONS])) === 0) {
              return operations;
            }

            return addOperation({ type, value: get('value')(shapeVariationsForAPI({ product, toImmutable })) });
          }

          case OPERATIONS[FAIRE].WIDTH: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.WIDTH, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.WIDTH, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          case OPERATIONS[FAIRE].MASS_UNIT: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.MASS_UNIT, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({ type, value: shapeFaireMassUnitForApi(value) });
          }

          case OPERATIONS[FAIRE].WEIGHT: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({ type, value });
          }

          default: {
            return operations;
          }
        }
      }

      case SHOPIFY: {
        switch (type) {
          case OPERATIONS[SHOPIFY].CATEGORY: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[SHOPIFY].PHOTOS.CHANGE_TO: {
            const value = product.getIn([FORM.PHOTOS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[SHOPIFY].VIDEOS.CHANGE_TO: {
            const value = product.getIn([FORM.VIDEOS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toJS() })
              : operations;
          }

          case OPERATIONS[SHOPIFY].TITLE: {
            const value = product.getIn([FORM.TITLE, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[SHOPIFY].DESCRIPTION: {
            const value = product.getIn([FORM.DESCRIPTION, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[SHOPIFY].PAGE_TITLE: {
            const value = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, VALUE]);
            const overset = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, OVERSET]);

            return !overset && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[SHOPIFY].META_DESCRIPTION: {
            const value = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, VALUE]);
            const overset = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, OVERSET]);

            return !overset && value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[SHOPIFY].TAGS.CHANGE_TO: {
            const tags = product.getIn([FORM.TAGS, VALUE], EMPTY.ORDERED_MAP);
            const newTags = product.getIn([FORM.TAGS, NEW], EMPTY.ORDERED_MAP);
            const value = tags.mergeWith(keepOld, newTags);

            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? tagsToList({ tags: value }) : tagsToArray(value) })
              : operations;
          }

          case OPERATIONS[SHOPIFY].PRODUCT_TYPE: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_TYPE, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[SHOPIFY].VENDOR: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE]);
            return value
              ? addOperation({ type, value })
              : operations;
          }

          case OPERATIONS[SHOPIFY].COLLECTIONS: {
            const value = product.getIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE]);
            return getSize(value) > 0
              ? addOperation({ type, value: toImmutable ? value : value.toArray() })
              : operations;
          }

          case OPERATIONS[SHOPIFY].VARIATIONS: {
            const variations = product.get(FORM.VARIATIONS);

            if (getSize(variations.get(COMBINATIONS)) === 0) {
              if (product.get('status') === STATUS[VELA].TEMPLATE) {
                const operation = {
                  type,
                  value: getInventoryOperationValue({ channel, inventory: product.get(FORM.INVENTORY), toImmutable }),
                };

                return addOperation(operation);
              }

              return operations;
            }

            const operationData = shapeVariationsForAPI({ product, toImmutable });
            const [value, shopifyMeta, templateId] = get('value', 'shopifyMeta', 'templateId')(operationData);
            const operation = { shopifyMeta, type, value };

            if (templateId) {
              operation.templateId = templateId;
            }

            return addOperation(operation);
          }

          case OPERATIONS[SHOPIFY].PRICE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ rounding: '', value })
                  : { rounding: '', value }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].CAP: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.CAP, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.CAP, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ rounding: '', value })
                  : { rounding: '', value }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].CPI: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.CPI, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.CPI, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ rounding: '', value })
                  : { rounding: '', value }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].CHARGE_TAX: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.CHARGE_TAX, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ taxable: !!value })
                  : { taxable: !!value }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].CONTINUE_SELLING: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.CONTINUE_SELLING, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ inventoryPolicy: value ? 'continue' : 'deny' })
                  : { inventoryPolicy: value ? 'continue' : 'deny' }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].TRACK_QUANTITY: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.TRACK_QUANTITY, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ inventoryManagement: value ? SHOPIFY : null })
                  : { inventoryManagement: value ? SHOPIFY : null }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].BARCODE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(value),
            });
          }

          case OPERATIONS[SHOPIFY].SKU: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, OVERSET]);

            if (overset || !value) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(value),
            });
          }

          case OPERATIONS[SHOPIFY].QUANTITY: {
            const trackQuantity = product.getIn([FORM.INVENTORY, ATTRIBUTE.TRACK_QUANTITY, VALUE]);
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, OVERSET]);

            if (overset || !value || !trackQuantity) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(value),
            });
          }

          case OPERATIONS[SHOPIFY].PHYSICAL: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET]);

            if (overset) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ requiresShipping: !!value })
                  : { requiresShipping: !!value }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].COUNTRY_CODE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.COUNTRY_CODE, VALUE]);
            const physical = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET]);

            if (overset || !value || !physical) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ countryCode: value })
                  : { countryCode: value }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].HS_CODE: {
            const value = product.getIn([FORM.INVENTORY, ATTRIBUTE.HS_CODE, VALUE]);
            const physical = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET]);

            if (overset || !value || !physical) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ hsCode: value })
                  : { hsCode: value }
              ),
            });
          }

          case OPERATIONS[SHOPIFY].WEIGHT: {
            const weight = product.getIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, VALUE]);
            const unit = product.getIn([FORM.INVENTORY, ATTRIBUTE.UNIT, VALUE]);
            const physical = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, VALUE]);
            const overset = product.getIn([FORM.INVENTORY, ATTRIBUTE.PHYSICAL, OVERSET]);

            if (overset || !weight || !physical) {
              return operations;
            }

            return addOperation({
              type,
              value: wrapInventoryOperationValue(
                toImmutable
                  ? Map({ weight, unit })
                  : { weight, unit }
              ),
            });
          }

          default: {
            return operations;
          }
        }
      }

      default: {
        return operations;
      }
    }
  }

  return operationTypes.reduce(reduceOperationTypes, result);
}
